import React from "react";
import "./Modal.css";
import { useI18n } from "./i18n";

/**
 * Generic modal.
 * - variant: "confirm" | "info" (default: "confirm")
 * - confirmLabel: label for the confirm button (default: "Confirm")
 * - onConfirm: only shown when provided AND variant === "confirm"
 *
 * If responseMessage is present, only a single "Close" button is shown.
 */
function Modal({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
  isProcessing,
  responseMessage,
  variant = "confirm",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmButtonClassName = "danger-btn",
  modalClassName = "",
  bodyClassName = "",
}) {
  const { t } = useI18n();
  if (!isOpen) return null;

  const showConfirmRow =
    !responseMessage && variant === "confirm" && typeof onConfirm === "function";
  const displayConfirmLabel = confirmLabel === "Confirm" ? t("common.confirm") : confirmLabel;
  const displayCancelLabel = cancelLabel === "Cancel" ? t("common.cancel") : cancelLabel;

  return (
    <div className="modal-overlay">
      <div className={`modal-container ${modalClassName}`.trim()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label={t("common.close")}>
            &times;
          </button>
        </div>

        <div className={`modal-body ${bodyClassName}`.trim()}>
          {message && (React.isValidElement(message) ? message : <p>{message}</p>)}

          {responseMessage && (
            <div className={`response-message ${responseMessage.type}`}>
              {responseMessage.text}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {showConfirmRow ? (
            <>
              <button
                className="btn secondary-btn"
                onClick={onClose}
                disabled={isProcessing}
              >
                {displayCancelLabel}
              </button>
              <button
                className={`btn ${confirmButtonClassName}`.trim()}
                onClick={onConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? t("common.processing") : displayConfirmLabel}
              </button>
            </>
          ) : (
            <button className="btn primary-btn" onClick={onClose}>
              {t("common.close")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Modal;
